import { OAuthButton } from '@/components/OAuthButton';
import { useXeroAuth } from '@/hooks/useXeroAuth';
import { useHighLevelAuth } from '@/hooks/useHighLevelAuth';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, AlertCircle, Info, ChevronDown } from 'lucide-react';
import { useState } from 'react';

export function ConnectionPage() {
  const xeroAuth = useXeroAuth();
  const highlevelAuth = useHighLevelAuth();
  const [faqOpen, setFaqOpen] = useState<string | null>(null);

  const toggleFaq = (id: string) => {
    setFaqOpen(faqOpen === id ? null : id);
  };

  const isLoading = xeroAuth.isLoading || highlevelAuth.isLoading;
  const hasError = xeroAuth.error || highlevelAuth.error;
  const isCrmConnected = highlevelAuth.connection?.isConnected;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Connection Management
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Connect your CRM first, then link your Xero account to enable data synchronization
        </p>
      </div>

      {hasError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Connection Error
              </h3>
              <p className="mt-1 text-sm text-red-700">
                {xeroAuth.error?.message || highlevelAuth.error?.message || 'Failed to load connection status'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Two-column layout for connections - CRM first */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Step 1: CRM Connection */}
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <svg className="h-6 w-6 text-[#7C3AED]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
                </svg>
                <h2 className="text-lg font-medium text-gray-900">
                  CRM Connection
                </h2>
              </div>
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                Step 1
              </span>
            </div>
            <OAuthButton provider="highlevel" />
          </div>

          {highlevelAuth.connection?.isConnected && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                CRM Details
              </h3>
              <dl className="grid grid-cols-1 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Location ID
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">
                    {highlevelAuth.connection.locationId}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Company ID
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">
                    {highlevelAuth.connection.companyId}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Connected
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDistanceToNow(new Date(highlevelAuth.connection.createdAt), {
                      addSuffix: true,
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className="inline-flex items-center space-x-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                      <CheckCircle className="h-3 w-3" />
                      <span>Active</span>
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {!highlevelAuth.connection?.isConnected && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                About CRM Integration
              </h3>
              <div className="prose prose-sm text-gray-500">
                <p>
                  Connect your CRM account first to establish your location identity.
                  This is required before connecting to Xero.
                </p>
                <h4 className="text-sm font-medium text-gray-900 mt-4">
                  Features:
                </h4>
                <ul>
                  <li>Access to your location data</li>
                  <li>Real-time webhook notifications</li>
                  <li>Secure OAuth 2.0 authentication</li>
                  <li>Automatic token refresh</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Xero Connection */}
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <svg className="h-6 w-6 text-[#13B5EA]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.514 0 10 4.486 10 10s-4.486 10-10 10S2 17.514 2 12 6.486 2 12 2z" />
                </svg>
                <h2 className="text-lg font-medium text-gray-900">
                  Xero Connection
                </h2>
              </div>
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                Step 2
              </span>
            </div>
            <OAuthButton
              provider="xero"
              disabled={!isCrmConnected}
              disabledMessage="Connect your CRM account first to enable Xero integration"
            />
          </div>

          {xeroAuth.connection?.isConnected && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Xero Details
              </h3>
              <dl className="grid grid-cols-1 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Organization
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {xeroAuth.connection.tenantName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">
                    {xeroAuth.connection.tenantType}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Connected
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDistanceToNow(new Date(xeroAuth.connection.createdAt), {
                      addSuffix: true,
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className="inline-flex items-center space-x-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                      <CheckCircle className="h-3 w-3" />
                      <span>Active</span>
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {!xeroAuth.connection?.isConnected && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                About Xero Integration
              </h3>
              <div className="prose prose-sm text-gray-500">
                <p>
                  Connect your Xero account to automatically sync contacts,
                  products, and invoices between your CRM and Xero.
                </p>
                <h4 className="text-sm font-medium text-gray-900 mt-4">
                  Features:
                </h4>
                <ul>
                  <li>Bi-directional sync for contacts, products, and invoices</li>
                  <li>Automatic or manual sync options</li>
                  <li>Conflict resolution strategies</li>
                  <li>Real-time sync status monitoring</li>
                </ul>
              </div>

              {/* Troubleshooting FAQ */}
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Troubleshooting
                </h4>
                <div className="space-y-2">
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleFaq('org-not-visible')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    >
                      <span>Can't see the organisation you want to select?</span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform ${
                          faqOpen === 'org-not-visible' ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {faqOpen === 'org-not-visible' && (
                      <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600 border-t border-gray-200">
                        <p>
                          If you can't see your desired Xero organisation during the connection process:
                        </p>
                        <ol className="list-decimal list-inside mt-2 space-y-1">
                          <li>Log out of Xero in this browser</li>
                          <li>Hard refresh this page (Ctrl+Shift+R or Cmd+Shift+R)</li>
                          <li>Click "Connect Xero" again</li>
                          <li>Log in with the account that has access to your desired organisation</li>
                        </ol>
                        <p className="mt-2 text-gray-500">
                          This ensures Xero prompts you to select which organisation to connect.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Next Steps - Only show when both are connected */}
      {xeroAuth.connection?.isConnected && highlevelAuth.connection?.isConnected && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Both Accounts Connected - Next Steps
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Configure sync settings in the Settings tab</li>
                  <li>Enable the entities you want to sync (contacts, products, invoices)</li>
                  <li>Set your preferred sync frequency</li>
                  <li>Monitor sync progress in the Dashboard</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partial Connection Warning - Only for Xero connected but not CRM (edge case) */}
      {xeroAuth.connection?.isConnected && !highlevelAuth.connection?.isConnected && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Partial Connection
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Both Xero and CRM accounts must be connected to enable data synchronization.
                Please connect your CRM account to continue.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
